import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPatientsPage, type PatientRow } from '../../../../services/patientsV2';

const SEARCH_DEBOUNCE_MS = 250;
// Was 8 - too low for an unfiltered browse of a practice's full roster (the
// list only ever showed the first 8 alphabetically, with no sign more
// existed). 50 matches the app's other practice/patient picker modals
// (CreateOneTimeDiscountModal, LinkOneTimeDiscountModal); the results list
// is already scrollable (see PatientPicker.tsx) to carry the larger set.
const PICKER_LIMIT = 50;

/** Backs the Linked patients card's search picker. Only fetches while `enabled`. */
export function usePatientPicker(practiceId: string, search: string, enabled: boolean) {
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const { data, isLoading } = useQuery({
        queryKey: ['v2/patients', 'picker', practiceId, debouncedSearch],
        queryFn: () =>
            fetchPatientsPage({
                page: 1,
                limit: PICKER_LIMIT,
                sort: 'patient',
                order: 'asc',
                practiceId,
                search: debouncedSearch || undefined
            }),
        enabled,
        staleTime: 30_000
    });

    return { results: (data?.data ?? []) as PatientRow[], isLoading };
}
